import {useState, useEffect, useRef} from 'react'
import {documentToPlainTextString} from '@contentful/rich-text-plain-text-renderer'
import {documentToHtmlString} from '@contentful/rich-text-html-renderer'
import type {GenericContent} from '../../../../../../brand/lib/types/contentful'
import {animateFadeIn, type AnimationElement} from '../../_utils/animations'
import {animateParallax, type AnimationParallaxElement} from '../../_utils/parallax'

import {Heading, Image, Prose} from '@primer/react-brand/esm'
import {TriangleDownIcon} from '@primer/octicons-react'
import {CtaButtons} from '../../_shared/CtaButtons/CtaButtons'
import {Label} from '../../_shared/Label/Label'
import {Spacer} from '../../_shared/Spacer/Spacer'

import Styles from './RiverAccordion.module.css'

type Props = {
  content: GenericContent
  items: GenericContent[]
  location: string
  index: number
  sectionIndex: number
}

export function RiverAccordion(props: Props) {
  const {content, index, items, location, sectionIndex} = props
  const {htmlId, heading, label, ctas} = content.fields

  const direction = htmlId // Used for setting left/right alignment

  const title = heading
  const eyebrow = label?.fields.text
  const ctaGroup = ctas?.at(0)

  // Accordion
  const [expandedIndex, setExpandedIndex] = useState<number>(0)
  const [previousIndex, setPreviousIndex] = useState<number | null>(null)

  const handleExpandItem = (itemIndex: number) => {
    setPreviousIndex(expandedIndex)
    setExpandedIndex(itemIndex)
  }

  // Animation
  const wrapper1Ref = useRef<HTMLDivElement>(null)
  const wrapper2Ref = useRef<HTMLDivElement>(null)

  const headingRef = useRef<HTMLDivElement>(null)
  const accordionRef = useRef<HTMLDivElement>(null)
  const ctaRef = useRef<HTMLDivElement>(null)
  const assetRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const elements1: AnimationElement[] = []
    if (headingRef.current) elements1.push({element: headingRef.current})
    if (accordionRef.current) elements1.push({element: accordionRef.current})
    if (ctaRef.current) elements1.push({element: ctaRef.current})
    const cleanup1 = wrapper1Ref.current ? animateFadeIn(elements1, wrapper1Ref.current) : undefined

    const elements2: AnimationParallaxElement[] = []
    if (assetRef.current) elements2.push({element: assetRef.current})
    const cleanup2 = wrapper2Ref.current ? animateParallax(elements2, wrapper2Ref.current) : undefined

    return () => {
      cleanup1?.()
      cleanup2?.()
    }
  }, [])

  return (
    <div className={Styles.RiverAccordion} data-direction={direction || (index % 2 === 0 ? 'left' : 'right')}>
      <div className={Styles.Cell} ref={wrapper1Ref}>
        <div className={Styles.Content}>
          {eyebrow && (
            <>
              <Label text={eyebrow} />
              <Spacer size="64px" size1280="0" />
            </>
          )}

          {title && (
            <>
              <Heading data-heading="2" as={sectionIndex === 0 ? 'h2' : 'h3'} ref={headingRef}>
                {title}
              </Heading>
              <Spacer size="16px" size1280="0" />
            </>
          )}

          {/* Accordion */}
          <div ref={accordionRef}>
            {items
              .filter(item => {
                const {subheading, text} = item.fields
                const summary = subheading ? documentToPlainTextString(subheading) : ''
                const description = text ? documentToPlainTextString(text) : ''
                return summary && description
              })
              .map((item, itemIndex) => {
                // Accordion item
                const {subheading, text} = item.fields
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                const summary = documentToPlainTextString(subheading!)
                if (!text) return null
                const description = documentToHtmlString(text)
                const imageAlt = item.fields.media?.[0]?.fields?.asset?.fields?.description || ''

                const isExpanded = expandedIndex === itemIndex

                return (
                  <div className={Styles.AccordionItem} key={item.sys?.id || itemIndex} data-expanded={isExpanded}>
                    <button
                      className={Styles.AccordionSummary}
                      onClick={() => handleExpandItem(itemIndex)}
                      aria-expanded={isExpanded}
                    >
                      {summary}
                      <TriangleDownIcon className={Styles.AccordionIcon} />
                    </button>

                    <div
                      className={Styles.AccordionContainer}
                      // [a11y] Indicates this element and all its descendants are inert (non-interactive) when not expanded
                      {...({inert: isExpanded ? undefined : 'true'} as React.HTMLAttributes<HTMLDivElement>)}
                    >
                      <div className={Styles.AccordionContent}>
                        <Prose className={Styles.Description} html={description} />

                        {/* A11y: Read image alt text */}
                        {imageAlt && <span className="sr-only">{imageAlt}</span>}
                      </div>
                    </div>
                  </div>
                )
              })}
          </div>

          {ctaGroup && (
            <>
              <Spacer size="64px" size1280="0" />
              <div ref={ctaRef}>
                <CtaButtons contentfulContent={ctaGroup} location={location} />
              </div>
            </>
          )}
        </div>
      </div>

      <div className={Styles.Cell} ref={wrapper2Ref}>
        {items.length > 0 && (
          <div ref={assetRef} data-animate="container">
            <div data-animate="item">
              {items.map((item, itemIndex) => {
                const image = item.fields.media?.[0]?.fields
                if (!image?.asset.fields.file.details?.image) return null // Only allow images

                return (
                  <Image
                    className={Styles.Image}
                    key={item.sys?.id || itemIndex}
                    src={image.asset.fields.file.url}
                    width={image.width || image.asset.fields.file.details?.image?.width || ''}
                    height={image.height || image.asset.fields.file.details?.image?.height || ''}
                    data-current={itemIndex === expandedIndex}
                    data-last={itemIndex === previousIndex}
                    alt=""
                    loading="lazy"
                  />
                )
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
